import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store';
import useTranslations from '../../../hooks/useTranslations';
import usePermissionRedirect from '../../../hooks/usePermissionRedirect';
import { FieldTypes, IFormFields } from '../../../models/form';
import Form from '../../../components/Container/Form/Form';
import Alert from '../../../components/UI/Alert/Alert';
import BackButton from '../../../components/UI/BackButton/BackButton';
import { IPage } from '../../../models/page';
import { SubCustomersRequestsActions } from '../../../store/entities/subCustomersRequests/subCustomersRequests.actions';
import { ISubCustomersRequest } from '../../../models/subCustomersRequest';
import { CONSTANTS } from '../../../utils/constants';
import { SUB_CUSTOMERS_REQUEST_STATUS_OPTIONS } from '../subCustomersRequestsConstants';
import { updateSubCustomersRequestValidation } from './updateSubCustomersRequestValidation';

const UpdateSubCustomersRequest = ({ permission, nextRoute }: IPage) => {
  const [show, setShow] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [existing, setExisting] = useState<ISubCustomersRequest | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  const { id } = useParams<{ id: string }>();
  usePermissionRedirect(permission, nextRoute);

  const { subCustomersRequests, selectedSubCustomersRequest } = useAppSelector((state) => state.subCustomersRequests);

  useEffect(() => {
    const numericId = Number(id);
    const cached = subCustomersRequests.find((r) => r.id === numericId) ?? selectedSubCustomersRequest ?? null;
    if (cached) {
      setExisting(cached);
    } else {
      (dispatch(SubCustomersRequestsActions.getSubCustomersRequestById(numericId)) as unknown as Promise<ISubCustomersRequest | null>).then(
        (data) => {
          if (data) setExisting(data);
        }
      );
    }
  }, [id]);

  const currentStatusKey =
    SUB_CUSTOMERS_REQUEST_STATUS_OPTIONS.find((o) => o.key === existing?.requestStatus)?.key ?? '';

  const fields: IFormFields = useMemo(
    () => ({
      initialValues: {
        requestStatus: currentStatusKey,
      },
      formData: [
        {
          type: FieldTypes.SELECT,
          id: 'requestStatus',
          label: 'subCustomersRequests.form.requestStatus',
          items: SUB_CUSTOMERS_REQUEST_STATUS_OPTIONS,
        },
      ],
    }),
    [existing]
  );

  const onSubmit = async (values: { requestStatus: string }, setSubmitting: (v: boolean) => void) => {
    const result = await dispatch(
      SubCustomersRequestsActions.updateSubCustomersRequest(Number(id), {
        requestStatus: values.requestStatus,
      })
    );

    setSubmitting(false);

    if (result !== undefined) {
      setAlertMessage(rcTranslate('subCustomersRequests.form.updateSuccess'));
      setShow(true);
      setTimeout(() => {
        setShow(false);
        navigate(
          '/' +
            CONSTANTS.ROUTES.SUB_CUSTOMERS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.SUB_CUSTOMERS_MANAGEMENT.CHILDREN.SUB_CUSTOMERS_LIST
        );
      }, 2500);
    }
  };

  return (
    <>
      <BackButton />
      <Form
        fields={fields}
        formValidation={updateSubCustomersRequestValidation}
        onSubmit={onSubmit}
        submitButtonTitle={rcTranslate('subCustomersRequests.form.updateButton')}
        entityIdentifier={{
          label: 'subCustomersRequests.form.entityIdentifier',
          id: id ?? '',
        }}
      />
      {show && <Alert message={alertMessage} color="success" />}
    </>
  );
};

export default UpdateSubCustomersRequest;
